import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../utils/axiosInstance";
import { Activity, Calendar, UserCircle } from "lucide-react";
import PageHeader from "../../Components/PageHeader";
import BranchInfo from "../../Components/BranchInfo";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./Dashboard.css";

function Dashboard() {
  const navigate = useNavigate();
  const [counts, setCounts] = useState({
    users: 0,
    departments: 0,
    doctors: 0,
    bookings: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Quick booking states
  const [doctors, setDoctors] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState("");
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [loadingDoctors, setLoadingDoctors] = useState(false);
  const [availableDates, setAvailableDates] = useState([]);
  const [loadingDates, setLoadingDates] = useState(false);

  // Branch info state
  const [branchData, setBranchData] = useState(null);

  useEffect(() => {
    const fetchCounts = async () => {
      try {
        setLoading(true);
        const response = await axiosInstance.get("/dashboard/counts");
        setCounts({
          users: response.data.counts.totalUsers || 0,
          departments: response.data.counts.totalDepartments || 0,
          doctors: response.data.counts.totalDoctors || 0,
          bookings: response.data.counts.totalBookings || 0,
        });
        setLoading(false);
      } catch (error) {
        console.error("Failed to fetch counts:", error);
        setError("Failed to load dashboard data. Please try again.");
        setLoading(false);
      }
    };

    fetchCounts();
  }, []);

  // Fetch branch information (only for branch_admin role)
  useEffect(() => {
    const fetchBranchInfo = async () => {
      try {
        const token = localStorage.getItem("token");
        console.log("Token exists:", !!token);

        if (token) {
          // Decode JWT to get branchId and role
          const payload = JSON.parse(atob(token.split(".")[1]));
          const { branchId, role } = payload;

          console.log("Decoded JWT - Role:", role, "BranchId:", branchId);

          // Only show branch info for branch_admin role
          if (role === "branch_admin" && branchId) {
            console.log("Fetching branch info for branch_admin...");

            // Fetch branch details - use the branches endpoint to get specific branch
            const branchResponse = await axiosInstance.get(
              `/hospitals/branches/${branchId}`
            );
            console.log("Branch response:", branchResponse.data);

            if (branchResponse.data) {
              const branch = branchResponse.data;
              const branchInfo = {
                hospitalName: branch.hospital?.name || "Medical Center",
                branchName: branch.name || "Main Branch",
                branchAddress:
                  branch.address || branch.hospital?.address || "N/A",
                branchPhone:
                  branch.phoneNumber || branch.hospital?.phoneNumber || "N/A",
                branchEmail: branch.email || branch.hospital?.email || "N/A",
              };
              console.log("Setting branch data:", branchInfo);
              setBranchData(branchInfo);
            } else {
              console.log("Branch not found");
            }
          } else {
            console.log("User is not branch_admin or has no branchId");
          }
        }
      } catch (error) {
        console.error("Failed to fetch branch info:", error);
      }
    };

    fetchBranchInfo();
  }, []);

  // Fetch doctors for quick booking
  useEffect(() => {
    const fetchDoctors = async () => {
      try {
        setLoadingDoctors(true);
        const response = await axiosInstance.get("/doctors/details");
        console.log("Fetched doctors:", response.data);
        setDoctors(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error("Failed to fetch doctors:", error);
      } finally {
        setLoadingDoctors(false);
      }
    };

    fetchDoctors();
  }, []);

  // Fetch available dates when doctor is selected
  useEffect(() => {
    const fetchAvailableDates = async () => {
      if (!selectedDoctor) {
        setAvailableDates([]);
        return;
      }

      try {
        setLoadingDates(true);
        const response = await axiosInstance.get(
          `/doctor/${selectedDoctor}/expanded-schedules`
        );

        // Extract dates with available slots
        const dates = response.data
          .filter((dateInfo) => dateInfo.availableSlots > 0)
          .map((dateInfo) => dateInfo.date);

        setAvailableDates(dates);

        // If current selected date is not in available dates, clear it
        if (!dates.includes(selectedDate)) {
          setSelectedDate("");
        }
      } catch (error) {
        console.error("Failed to fetch available dates:", error);
        setAvailableDates([]);
      } finally {
        setLoadingDates(false);
      }
    };

    fetchAvailableDates();
  }, [selectedDoctor]);

  // Handle date change with validation
  const handleDateChange = (date) => {
    if (!date) {
      setSelectedDate("");
      return;
    }

    // Use local date to avoid timezone issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const dateStr = `${year}-${month}-${day}`;

    if (!selectedDoctor) {
      alert("Please select a doctor first");
      return;
    }

    // Check if the selected date is in available dates
    if (availableDates.includes(dateStr)) {
      setSelectedDate(dateStr);
    } else {
      alert(
        "This date is not available for the selected doctor. Please choose a highlighted date."
      );
    }
  };

  // Function to highlight available dates
  const getDayClassName = (date) => {
    // Use local date to avoid timezone issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const dateStr = `${year}-${month}-${day}`;

    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(
      today.getMonth() + 1
    ).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

    if (dateStr < todayStr) {
      return "react-datepicker__day--disabled"; // Past dates
    }

    if (availableDates.includes(dateStr)) {
      return "available-date"; // Available dates - green
    }

    return selectedDoctor ? "unavailable-date" : ""; // Unavailable dates - red only if doctor selected
  };

  const handleViewSlots = () => {
    if (!selectedDoctor) {
      alert("Please select a doctor");
      return;
    }
    if (!selectedDate) {
      alert("Please select a date");
      return;
    }
    navigate(
      `/dashboard/doctor/${selectedDoctor}/schedules/${selectedDate}/slots`
    );
  };

  const userIcon = (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="h-6 w-6 text-blue-600"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M5 20h14c1 0 1.78-1.08 1.98-2.56l.01-.12C20 15.28 17.64 14 15 14H9c-2.64 0-5 1.28-5.98 3.32-.2 1.48.98 2.68 1.98 2.68zM12 12a4 4 0 100-8 4 4 0 000 8z"
      />
    </svg>
  );

  const departmentIcon = (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-6 w-6 text-green-600"
    >
      <path d="M3 21h18" />
      <path d="M5 21V9a2 2 0 012-2h10a2 2 0 012 2v12" />
      <path d="M9 3h6v4H9z" />
      <path d="M10 11h4" />
      <path d="M10 15h4" />
    </svg>
  );

  const doctorIcon = (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-6 w-6 text-purple-600"
    >
      <circle cx="9" cy="7" r="4" />
      <circle cx="15" cy="7" r="4" />
      <path d="M2 20c0-4 5-6 10-6s10 2 10 6" />
    </svg>
  );

  const bookingIcon = (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-6 w-6 text-amber-600"
    >
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
      <line x1="16" y1="2" x2="16" y2="6"></line>
      <line x1="8" y1="2" x2="8" y2="6"></line>
      <line x1="3" y1="10" x2="21" y2="10"></line>
    </svg>
  );

  // Dashboard stat card component
  const StatCard = ({ icon, title, value, bgColor }) => (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
      <div className="p-5">
        <div className="flex items-center">
          <div className={`rounded-full p-3 mr-4 ${bgColor}`}>{icon}</div>
          <div>
            <p className="text-sm font-medium text-gray-500">{title}</p>
            <p className="text-2xl font-bold text-gray-800">
              {loading ? (
                <div className="animate-pulse h-8 w-16 bg-gray-200 rounded"></div>
              ) : (
                value
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center p-6 bg-white rounded-lg shadow-sm">
          <div className="text-red-500 mb-4">
            <svg
              className="w-12 h-12 mx-auto"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <p className="text-lg font-medium text-gray-900">Error</p>
          <p className="text-gray-600 mt-2">{error}</p>
          <button
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            onClick={() => window.location.reload()}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Page Header */}
        <PageHeader
          title="Dashboard"
          subtitle="Welcome to your hospital management dashboard"
          showBackButton={false}
        />

        {/* Branch Info Display */}
        {branchData && (
          <div className="mb-6">
            <BranchInfo branchData={branchData} />
          </div>
        )}

        {/* Stats Grid - Responsive for all screens */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            icon={userIcon}
            title="Users"
            value={counts.users}
            bgColor="bg-blue-100"
          />
          <StatCard
            icon={departmentIcon}
            title="Departments"
            value={counts.departments}
            bgColor="bg-green-100"
          />
          <StatCard
            icon={doctorIcon}
            title="Doctors"
            value={counts.doctors}
            bgColor="bg-purple-100"
          />
          <StatCard
            icon={bookingIcon}
            title="Bookings"
            value={counts.bookings}
            bgColor="bg-amber-100"
          />
        </div>

        {/* Quick Book Appointment Section */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-8">
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-5">
            <div className="flex items-center">
              <Calendar className="w-6 h-6 text-white mr-3" />
              <h2 className="text-xl font-semibold text-white">
                Quick Book Appointment
              </h2>
            </div>
            <p className="text-blue-100 mt-1 text-sm">
              Select a doctor and date to view available slots
            </p>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Doctor Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <div className="flex items-center gap-2">
                    <UserCircle className="w-4 h-4" />
                    Select Doctor
                  </div>
                </label>
                <select
                  value={selectedDoctor}
                  onChange={(e) => setSelectedDoctor(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  disabled={loadingDoctors}
                >
                  <option value="">
                    {loadingDoctors ? "Loading doctors..." : "Choose a doctor"}
                  </option>
                  {doctors.map((doctor) => (
                    <option key={doctor.id} value={doctor.id}>
                      {doctor.name} | {doctor.departmentName || "N/A"} |{" "}
                      {doctor.qualification || "General"}
                    </option>
                  ))}
                </select>
              </div>

              {/* Date Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Select Date
                    {loadingDates && (
                      <span className="text-xs text-gray-500">
                        (Loading...)
                      </span>
                    )}
                  </div>
                </label>
                <DatePicker
                  selected={selectedDate ? new Date(selectedDate) : null}
                  onChange={handleDateChange}
                  minDate={new Date()}
                  disabled={!selectedDoctor || loadingDates}
                  dayClassName={getDayClassName}
                  dateFormat="yyyy-MM-dd"
                  placeholderText={
                    !selectedDoctor ? "Select a doctor first" : "Choose a date"
                  }
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition disabled:bg-gray-100 disabled:cursor-not-allowed"
                  calendarClassName="custom-calendar"
                  wrapperClassName="w-full"
                />
                {!selectedDoctor && (
                  <p className="text-xs text-gray-500 mt-1">
                    Select a doctor to see available dates
                  </p>
                )}
                {selectedDoctor &&
                  !loadingDates &&
                  availableDates.length > 0 && (
                    <div className="text-xs mt-2 space-y-1">
                      <p className="text-blue-600">
                        {availableDates.length} date(s) available
                      </p>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1">
                          <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                          <span className="text-gray-600">Available</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <div className="w-3 h-3 bg-red-300 rounded-full"></div>
                          <span className="text-gray-600">Not Available</span>
                        </div>
                      </div>
                    </div>
                  )}
                {selectedDoctor &&
                  !loadingDates &&
                  availableDates.length === 0 && (
                    <p className="text-xs text-amber-600 mt-1">
                      This doctor has no available slots
                    </p>
                  )}
              </div>

              {/* Action Button */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 opacity-0">
                  Action
                </label>
                <button
                  onClick={handleViewSlots}
                  disabled={
                    !selectedDoctor ||
                    !selectedDate ||
                    loadingDoctors ||
                    loadingDates
                  }
                  className="w-full px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors shadow-sm hover:shadow-md font-medium"
                >
                  View Available Slots
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
